import React, { Fragment, memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { FC } from 'react';

import type {
	AnnotationByMatches,
	InlineCommentSelectionComponentProps,
} from '@atlaskit/editor-common/types';
import type { AddMarkStep } from '@atlaskit/editor-prosemirror/transform';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	useAnnotationsDispatch,
	useAnnotations,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import {
	ADD_INLINE_COMMENT_EXPERIENCE,
	ADD_INLINE_COMMENT_LOAD_EXPERIENCE,
	ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { startEditorExperiences } from '@confluence/experience-tracker/entry-points/startExperienceForRoute.ts';
import { expVal } from '@confluence/feature-experiments';
import {
	InlineCommentMode,
	InlineCommentFramework,
} from '@confluence/inline-comments-utils/entry-points/enum.ts';
import type { CommentCreationLocation } from '@confluence/inline-comments-queries/entry-points/CreateInlineCommentMutation.ts';
import { HighlightActions } from '@confluence/highlight-actions/entry-points/HighlightActions.ts';
import { HighlightActionsSnippetButton } from '@confluence/snippets/entry-points/HighlightActionsSnippetButton.ts';
import { HighlightActionsSnippetDropdownItem } from '@confluence/snippets/entry-points/HighlightActionsSnippetDropdownItem.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { ReattachCommentContext } from '@confluence/comment-context/entry-points/ReattachCommentContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useRendererRemixContext } from '@confluence/malleable-ui-common/entry-points/RendererRemixContainer.ts';

import { CreateComment } from '../CreateComment.tsx';

export type SelectionComponentProps = {
	pageId: string;
	isArchived?: boolean;
	contentType?: 'page' | 'blogpost' | 'whiteboard' | 'database' | 'slide' | 'embed';
	lastModifiedDate?: string | null;
	onCommentButtonMount?: () => void;
	isOffline?: boolean;
};

export type SelectionOptions = AnnotationByMatches & {
	createdFrom: CommentCreationLocation;
	step?: AddMarkStep;
	inlineNodeTypes?: string[];
	targetNodeType?: string;
};

export const SelectionComponent: FC<
	InlineCommentSelectionComponentProps & SelectionComponentProps
> = memo(
	({
		pageId,
		isArchived,
		range,
		draftRange,
		isAnnotationAllowed,
		wrapperDOM,
		contentType,
		inlineNodeTypes,
		lastModifiedDate,
		onCommentButtonMount,
		isOffline = false,
	}) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { isInReattachMode } = useContext(ReattachCommentContext);
		const {
			setSelectionOptionsForCreateEditor,
			setShowHighlightActions,
			setShowCreateComponent,
			handleSaveSuccess,
			handleClose,
		} = useAnnotationsDispatch();

		const { showCreateComponent, showHighlightActions, selectionOptions, stepGenerationError } =
			useAnnotations();

		const annotationElement = useRef<HTMLElement | null>(null);
		const prevPageIdRef = useRef(pageId);
		const [allowAnnotationOnSelection, setAllowAnnotationOnSelection] =
			useState(isAnnotationAllowed);

		const { openRemixModal } = useRendererRemixContext();

		const { edition } = useSessionData();
		const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();

		useEffect(() => {
			// If we're in reattach mode or we have no selection do nothing
			if (isInReattachMode() || !range) {
				setShowHighlightActions(false);
				return;
			}

			const { startContainer, endContainer } = range;
			let nodeToCheck: HTMLElement | null = null;

			// If we're highlighting an inline-extension, don't allow annotations
			// Check the startContainer first
			if (startContainer.nodeType === Node.TEXT_NODE) {
				nodeToCheck = startContainer.parentElement;
			} else {
				nodeToCheck = startContainer as HTMLElement;
			}

			if (nodeToCheck && nodeToCheck.closest('.ak-renderer-extension')) {
				if (editorExperiment('comment_on_bodied_extensions', true, { exposure: true })) {
					// `:not(:has(` currently causes nwsapi to fail (used in jsdom)
					// so the following is a workaround until that is fixed
					if (
						!nodeToCheck
							.closest('.ak-renderer-extension')
							?.querySelector('[data-inline-comments-target=true]')
					) {
						setAllowAnnotationOnSelection(false);
						setShowHighlightActions(true);
						return;
					}
				} else {
					setAllowAnnotationOnSelection(false);
					setShowHighlightActions(true);
					return;
				}
			}

			// Next check the endContainer if the start wasn't on an inline-extension
			if (endContainer.nodeType === Node.TEXT_NODE) {
				nodeToCheck = endContainer.parentElement;
			} else {
				nodeToCheck = endContainer as HTMLElement;
			}

			if (nodeToCheck && nodeToCheck.closest('.ak-renderer-extension')) {
				if (editorExperiment('comment_on_bodied_extensions', true, { exposure: true })) {
					// `:not(:has(` currently causes nwsapi to fail (used in jsdom)
					// so the following is a workaround until that is fixed
					if (
						!nodeToCheck
							.closest('.ak-renderer-extension')
							?.querySelector('[data-inline-comments-target=true]')
					) {
						setAllowAnnotationOnSelection(false);
					} else {
						// If everything passes, set it to what was passed by the annotationProvider
						setAllowAnnotationOnSelection(isAnnotationAllowed);
					}
				} else {
					setAllowAnnotationOnSelection(false);
				}
			} else if (
				nodeToCheck?.closest('[data-sync-block-renderer]') &&
				expVal('platform_synced_block', 'isEnabled', false)
			) {
				setAllowAnnotationOnSelection(false);
			} else {
				// If everything passes, set it to what was passed by the annotationProvider
				setAllowAnnotationOnSelection(isAnnotationAllowed);
			}

			setShowHighlightActions(true);
		}, [
			range,
			isAnnotationAllowed,
			isInReattachMode,
			setShowHighlightActions,
			setAllowAnnotationOnSelection,
		]);

		// TODO: Fix me
		// This currently doesn't do anything to position the sidebar
		// We SHOULD be calculating this annotationElement and positioning based on it
		useEffect(() => {
			if (!draftRange) {
				return;
			}

			const rangeContainer = draftRange?.startContainer;

			if (rangeContainer) {
				if (rangeContainer.nodeType === Node.TEXT_NODE) {
					annotationElement.current = rangeContainer.parentElement;
				} else {
					annotationElement.current = rangeContainer as HTMLElement;
				}
			}
		}, [draftRange]);

		useEffect(() => {
			const showCreateComponent = Boolean(selectionOptions);

			if (showCreateComponent) {
				startEditorExperiences(
					{
						name: ADD_INLINE_COMMENT_EXPERIENCE,
						attributes: {
							mode: InlineCommentMode.VIEW,
							framework: InlineCommentFramework.ANNOTATION_PROVIDER,
							edition,
						},
					},
					{
						name: ADD_INLINE_COMMENT_LOAD_EXPERIENCE,
					},
					{
						name: ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
					},
				);
			}

			setShowCreateComponent(showCreateComponent);
		}, [edition, selectionOptions, pageId, createAnalyticsEvent, setShowCreateComponent]);

		useEffect(() => {
			const prevPageId = prevPageIdRef.current;

			// Whenever the pageId changes, we need to close anything that's open
			if (pageId !== prevPageId) {
				handleClose();
			}

			return () => {
				if (pageId !== prevPageId) {
					handleClose();
				}
			};
			// We only want to listen to the pageId changing
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [pageId]);

		const handleCommentButtonPress = useCallback(() => {
			setSelectionOptionsForCreateEditor({
				experienceAttributes: { entryPoint: 'highlightActions', edition },
			});
		}, [setSelectionOptionsForCreateEditor, edition]);

		const handleRemixButtonPress = useCallback(() => {
			// TODO MAX-7: Properly set selection context
			const selection = window.getSelection();
			openRemixModal(selection?.toString() ?? '');
		}, [openRemixModal]);

		// Show an error message if the generation of a step caused an error
		if (stepGenerationError) {
			// Do something error-y
		}

		return (
			<Fragment>
				{showCreateComponent && (
					<CreateComment
						pageId={pageId}
						annotationElement={wrapperDOM}
						selectionOptions={selectionOptions}
						onCreate={(annotationId: string, commentData?: any) => {
							handleSaveSuccess(annotationId, commentData);
						}}
						onClose={handleClose}
						isOffline={isOffline}
					/>
				)}
				{range && showHighlightActions && (
					<HighlightActions
						contentId={pageId}
						range={range}
						isAnnotationAllowed={allowAnnotationOnSelection}
						onCommentButtonPress={handleCommentButtonPress}
						onRemixButtonPress={handleRemixButtonPress}
						isArchived={isArchived}
						contentType={contentType}
						inlineNodeTypes={inlineNodeTypes}
						isFabric
						lastModifiedDate={lastModifiedDate}
						onCommentButtonMount={onCommentButtonMount}
						snippetSlots={
							isEmbeddedConfluence
								? undefined
								: {
										button: <HighlightActionsSnippetButton contentId={pageId} />,
										dropdownItem: <HighlightActionsSnippetDropdownItem contentId={pageId} />,
									}
						}
					/>
				)}
			</Fragment>
		);
	},
);
